// src/pages/About.jsx
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Target, Sparkles, Shield, Award } from 'lucide-react';

const About = () => {
  /* ========= BRAND TOKENS ========= */
  const brandTeal = '#1A9E96';            /* deep teal — matches logo */
  const brandTealDark = '#157F79';        /* hover / deeper */
  const heroGradient = 'linear-gradient(135deg, #49E9E9 0%, #00C9A7 50%, #6B7280 100%)';

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container>
        {/* Hero Section - Centered */}
        <div className="text-center mb-5">
          <div className="rounded-4 overflow-hidden shadow-lg mx-auto mb-4" style={{ maxWidth: '800px' }}>
            <img
              src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200"
              alt="Team collaboration"
              className="w-100 object-fit-cover"
              style={{ height: '320px' }}
            />
          </div>
          <h1 className="display-5 fw-bold text-dark mb-3">About Us</h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
            Fast Global Resume was designed and engineered to make resume building fast, private, and painless.
          </p>
          <p className="text-secondary fs-5 mt-3">
            Helping professionals build standout resumes.
          </p>
        </div>

        {/* Stats — all in brand teal */}
        <Row className="g-3 mb-5">
          <Col xs={6} md={3}>
            <div className="text-center bg-light rounded-3 p-3">
              <div className="fs-3 fw-bold" style={{ color: brandTeal }}>100k+</div>
              <div className="small text-muted">Users</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center bg-light rounded-3 p-3">
              <div className="fs-3 fw-bold" style={{ color: brandTeal }}>70+</div>
              <div className="small text-muted">Industries</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center bg-light rounded-3 p-3">
              <div className="fs-3 fw-bold" style={{ color: brandTeal }}>50+</div>
              <div className="small text-muted">Countries</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center bg-light rounded-3 p-3">
              <div className="fs-3 fw-bold" style={{ color: brandTeal }}>4.9/5</div>
              <div className="small text-muted">Rating</div>
            </div>
          </Col>
        </Row>

        {/* Mission - Image & Text Side by Side */}
        <Row className="g-5 align-items-center mb-5">
          <Col lg={6}>
            <div className="rounded-4 overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200"
                alt="Our mission"
                className="w-100 object-fit-cover"
                style={{ height: '320px' }}
              />
            </div>
          </Col>
          <Col lg={6}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Target size={20} style={{ color: brandTeal }} />
              <span
                className="small fw-semibold text-uppercase"
                style={{ letterSpacing: '0.05em', color: brandTeal }}
              >
                Our Mission
              </span>
            </div>
            <h2 className="h2 fw-bold mb-3">Empowering Job Seekers Worldwide</h2>
            <p className="text-muted mb-3" style={{ lineHeight: 1.7 }}>
              Our goal is to give you a premium resume builder without tracking your personal data.
              At Fast Global Resume, our mission is to empower every job seeker with the tools to present
              their best professional self.
            </p>
            <p className="text-muted" style={{ lineHeight: 1.7 }}>
              We combine thoughtful design with smart technology to help you craft resumes that get noticed.
              Your success is the measure of ours.
            </p>
          </Col>
        </Row>

        {/* Features */}
        <div className="text-center mb-4">
          <h2 className="h2 fw-bold mb-2">Built Different</h2>
          <p className="text-muted">Here's what makes Fast Global Resume unique</p>
        </div>

        <Row className="g-4 mb-5">
          <Col md={4}>
            <div className="bg-white rounded-3 shadow-sm border p-4 text-center h-100">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: 48, height: 48, backgroundColor: 'rgba(26, 158, 150, 0.12)', color: brandTeal }}
              >
                <Sparkles size={24} />
              </div>
              <h3 className="h6 fw-semibold mb-2">Fast & Easy</h3>
              <p className="text-muted small mb-0">
                Build a professional resume in under 10 minutes with our intuitive builder.
              </p>
            </div>
          </Col>
          <Col md={4}>
            <div className="bg-white rounded-3 shadow-sm border p-4 text-center h-100">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: 48, height: 48, backgroundColor: 'rgba(26, 158, 150, 0.12)', color: brandTeal }}
              >
                <Shield size={24} />
              </div>
              <h3 className="h6 fw-semibold mb-2">Privacy First</h3>
              <p className="text-muted small mb-0">
                100% private – your data stays yours. No tracking.
              </p>
            </div>
          </Col>
          <Col md={4}>
            <div className="bg-white rounded-3 shadow-sm border p-4 text-center h-100">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: 48, height: 48, backgroundColor: 'rgba(26, 158, 150, 0.12)', color: brandTeal }}
              >
                <Award size={24} />
              </div>
              <h3 className="h6 fw-semibold mb-2">ATS-Friendly</h3>
              <p className="text-muted small mb-0">
                Our templates are optimized to pass applicant tracking systems.
              </p>
            </div>
          </Col>
        </Row>

        {/* CTA — uses home hero gradient */}
        <div
          className="rounded-4 p-5 text-center text-white"
          style={{ background: heroGradient }}
        >
          <h2 className="h3 fw-bold mb-3">Ready to Build Your Resume?</h2>
          <p className="mb-4" style={{ opacity: 0.95 }}>
            Join 100,000+ professionals who got hired using Fast Global Resume
          </p>
          <a
            href="/signup"
            className="btn fw-semibold px-4 py-2"
            style={{
              backgroundColor: '#ffffff',
              color: brandTeal,
              borderColor: '#ffffff',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = brandTealDark;
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.borderColor = brandTealDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = brandTeal;
              e.currentTarget.style.borderColor = '#ffffff';
            }}
          >
            Get Started Free
          </a>
        </div>
      </Container>
    </div>
  );
};

export default About;